import type { ParameterlessTranslationKey } from '@orus.eu/translations'
import type { Prettify } from '@orus.eu/utils'

import type { Label } from './label.ts'

/** The value of one answer. A single-choice answer is the id of the chosen option. */
export type AnswerValue = string

/** The answers by question id, unchecked, as a draft or a request holds them. */
export type Answers = Readonly<Record<string, AnswerValue>>

export type SingleChoiceOption<OptionId extends string> = {
  readonly id: OptionId
  readonly label: ParameterlessTranslationKey
}

export type SingleChoiceQuestion<OptionId extends string, LabelParameters> = {
  readonly kind: 'single-choice'
  /** Absent when the screen title asks the question. */
  readonly label: Label<LabelParameters> | undefined
  readonly tooltip: Label<LabelParameters> | undefined
  readonly options: readonly SingleChoiceOption<OptionId>[]
  readonly isVisible: (answers: Answers) => boolean
}

export type Question<LabelParameters> = SingleChoiceQuestion<string, LabelParameters>

/** The answer type of a question: for a single-choice question, the union of its option ids. */
export type AnswerOf<SomeQuestion> = SomeQuestion extends {
  readonly kind: 'single-choice'
  readonly options: readonly SingleChoiceOption<infer OptionId>[]
}
  ? OptionId
  : never

/** The answers typed by a set of questions, each one optional. */
export type AnswersOf<Questions> = Prettify<{
  readonly [QuestionId in keyof Questions]?: AnswerOf<Questions[QuestionId]>
}>

/** A single-choice question as a definition writes it, its predicate typed by the answers it may read. */
export type SingleChoiceSpecification<OptionId extends string, LabelParameters, ReadableAnswers> = {
  readonly kind: 'single-choice'
  /** Left out when the screen title asks the question. */
  readonly label?: Label<LabelParameters>
  readonly tooltip?: Label<LabelParameters>
  readonly options: readonly SingleChoiceOption<OptionId>[]
  /**
   * Reads the answers to the questions defined before this one. Defaults to always visible. A visible question is
   * required, and a hidden one is not asked: its answer is dropped.
   */
  readonly isVisible?: (answers: ReadableAnswers) => boolean
}

export function isAcceptedAnswer(
  question: Pick<Question<never>, 'kind' | 'options'>,
  value: unknown,
): value is AnswerValue {
  switch (question.kind) {
    case 'single-choice':
      return typeof value === 'string' && question.options.some((option) => option.id === value)
  }
}
